import { t } from '@grafana/i18n';
import { handleError } from 'app/api/utils';
import { createSuccessNotification } from 'app/core/copy/appNotification';
import { notifyApp } from 'app/core/reducers/appNotification';

import { generatedAPI } from './endpoints.gen';

export const queriesAPIv1 = generatedAPI.enhanceEndpoints({
  endpoints: {
    // Need to mutate the generated query to set the Content-Type header correctly
    updateQuery: (endpointDefinition) => {
      const originalQuery = endpointDefinition.query;
      if (originalQuery) {
        endpointDefinition.query = (requestOptions) => ({
          ...originalQuery(requestOptions),
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
        });
      }
      endpointDefinition.onQueryStarted = async (_, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          dispatch(
            notifyApp(
              createSuccessNotification(t('saved-queries.details.update-success', 'Query successfully updated'))
            )
          );
        } catch (e) {
          handleError(e, dispatch, t('saved-queries.details.update-error', 'Failed to update query'));
        }
      };
    },
    createQuery: (endpointDefinition) => {
      endpointDefinition.onQueryStarted = async (_, { queryFulfilled, dispatch }) => {
        try {
          // No optimistic list patch: the list is paginated (see useSavedQueriesData), so a created
          // query does not necessarily belong on the first page — appending it there would show a
          // row the server would never return, and the drain could then return it again as a
          // duplicate. `invalidatesTags: ['Query']` refetches the list instead, and
          // SavedQueriesLibrary keeps the new uid selected until it arrives.
          await queryFulfilled;
          dispatch(
            notifyApp(
              createSuccessNotification(
                t('saved-queries.details.save-success', 'Query successfully saved to the library')
              )
            )
          );
        } catch (e) {
          handleError(e, dispatch, t('saved-queries.details.create-error', 'Failed to save query'));
        }
      };
    },
    deleteQuery: (endpointDefinition) => {
      endpointDefinition.onQueryStarted = async (_, { queryFulfilled, dispatch }) => {
        try {
          await queryFulfilled;
          dispatch(notifyApp(createSuccessNotification(t('saved-queries.details.delete-success', 'Query deleted'))));
        } catch (e) {
          handleError(e, dispatch, t('saved-queries.details.delete-error', 'Failed to delete query'));
        }
      };
    },
  },
});

export const {
  useCreateQueryMutation,
  useDeleteQueryMutation,
  useGetQueryQuery,
  useLazyListQueryQuery,
  useListQueryQuery,
  useUpdateQueryMutation,
} = queriesAPIv1;

export { QUERY_LIBRARY_PAGE_LIMIT } from './baseAPI';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type { QuerySpec, Query, ListQueryApiResponse } from './endpoints.gen';
